import React, { useContext } from "react";
import { LanguageContext } from "../LanguageContext";
import { Link } from "react-router-dom";
import "./InformationalPages.css";
import SEO from "../components/SEO";

const Privacypolicy = () => {
  const { language } = useContext(LanguageContext);

  const metaTitle = "Privacy Policy - Snapseed QR Codes | Data Protection & User Rights";
  const metaDescription = "Learn how Snapseedqrcodes.download collects, stores, and protects your personal data while ensuring user privacy and security.";
  const metaUrl = "https://www.snapseedqrcodes.download/privacy-policy";
  const metaImage = "https://www.snapseedqrcodes.download/snapseedqrcodes_logo.webp";

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.snapseedqrcodes.download/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Privacy Policy",
        "item": metaUrl
      }
    ]
  };

  return (
    <div className="informational-page">
      <SEO
        title={metaTitle}
        description={metaDescription}
        url={metaUrl}
        image={metaImage}
        schemaMarkup={breadcrumbSchema}
      />

      <h1>{language === "english" ? "Privacy Policy" : "गोपनीयता नीति"}</h1>

      <h3>{language === "english" ? "Introduction" : "परिचय"}</h3>
      <p className="section-description searchable-content">
        {language === "english"
          ? <>Welcome to Snapseedqrcodes.download. Please also review our <Link to="/terms">Terms & Conditions</Link> and <Link to="/contact">Contact</Link> page for additional clarity.</>
          : <>Snapseedqrcodes.download में आपका स्वागत है। कृपया अतिरिक्त जानकारी के लिए हमारी <Link to="/terms">नियम और शर्तें</Link> और <Link to="/contact">संपर्क करें</Link> पेज भी देखें।</>
        }
      </p>

      <h3>{language === "english" ? "Acceptance of Terms" : "शर्तों की स्वीकृति"}</h3>
      <p className="section-description searchable-content">
        {language === "english"
          ? "By using our website, you accept the terms in this privacy policy. If you disagree, please discontinue use."
          : "इस वेबसाइट का उपयोग करके, आप इस गोपनीयता नीति की शर्तों को स्वीकार करते हैं। यदि आप असहमत हैं, तो कृपया उपयोग बंद करें।"}
      </p>

      <h3>{language === "english" ? "Information Collection and Management" : "जानकारी संग्रहण और प्रबंधन"}</h3>
      <p className="section-description searchable-content">
        {language === "english"
          ? "We collect only the information you voluntarily provide, such as through contact forms. We do not sell or lease your data."
          : "हम केवल वही जानकारी एकत्र करते हैं जो आप स्वेच्छा से प्रदान करते हैं, जैसे संपर्क फ़ॉर्म के माध्यम से। हम आपकी जानकारी को बेचते या किराए पर नहीं देते।"}
      </p>

      <h3>{language === "english" ? "Purpose of Information Usage" : "जानकारी के उपयोग का उद्देश्य"}</h3>
      <p className="section-description searchable-content">
        {language === "english"
          ? "Your data is used solely to respond to inquiries and manage communication. It may be shared only to fulfill specific user requests."
          : "आपका डेटा केवल पूछताछ का उत्तर देने और संचार प्रबंधन के लिए उपयोग किया जाता है। इसे केवल उपयोगकर्ता के अनुरोधों को पूरा करने के लिए साझा किया जा सकता है।"}
      </p>

      <h3>{language === "english" ? "Types of Information Collected" : "एकत्रित जानकारी के प्रकार"}</h3>
      <ul>
        <li>
          {language === "english"
            ? "Personal Information (name, email, etc.)"
            : "व्यक्तिगत जानकारी (नाम, ईमेल आदि)"}
        </li>
        <li>
          {language === "english"
            ? "Non-Personal Data (IP address, browser, device)"
            : "गैर-व्यक्तिगत डेटा (आईपी पता, ब्राउज़र, डिवाइस)"}
        </li>
      </ul>

      <h3>{language === "english" ? "External Content Integration" : "बाहरी सामग्री एकीकरण"}</h3>
      <p className="section-description searchable-content">
        {language === "english"
          ? "Some content may come from third parties. These providers may collect your data through cookies and tracking."
          : "कुछ सामग्री तृतीय पक्षों से हो सकती है। वे कुकीज़ और ट्रैकिंग के माध्यम से आपका डेटा एकत्र कर सकते हैं।"}
      </p>

      <h3>{language === "english" ? "Information Control Rights" : "जानकारी नियंत्रण अधिकार"}</h3>
      <ul>
        <li>{language === "english" ? "View or request deletion of your data" : "अपने डेटा को देखें या हटाने का अनुरोध करें"}</li>
        <li>{language === "english" ? "Update or correct personal data" : "व्यक्तिगत डेटा को अपडेट या सही करें"}</li>
        <li>{language === "english" ? "Express privacy-related concerns" : "गोपनीयता से संबंधित चिंताएं व्यक्त करें"}</li>
      </ul>

      <h3>{language === "english" ? "Security Measures" : "सुरक्षा उपाय"}</h3>
      <p className="section-description searchable-content">
        {language === "english"
          ? "We use HTTPS and SSL to protect sensitive data. However, no method is 100% secure online."
          : "हम संवेदनशील डेटा की सुरक्षा के लिए HTTPS और SSL का उपयोग करते हैं। हालांकि, कोई भी ऑनलाइन तरीका 100% सुरक्षित नहीं है।"}
      </p>

      <h3>{language === "english" ? "Policy Modifications" : "नीति संशोधन"}</h3>
      <p className="section-description searchable-content">
        {language === "english"
          ? "We may update this policy and will notify users accordingly. Please check back regularly."
          : "हम इस नीति को अपडेट कर सकते हैं और उपयोगकर्ताओं को सूचित करेंगे। कृपया समय-समय पर पुनः देखें।"}
      </p>

      <h3>{language === "english" ? "Consent Declaration" : "सहमति घोषणा"}</h3>
      <p className="section-description searchable-content">
        {language === "english"
          ? <>Using our site means you agree to this Privacy Policy. For questions, please <Link to="/contact">contact us</Link> or visit <Link to="/about">About Us</Link>.</>
          : <>हमारी साइट का उपयोग करने का अर्थ है कि आप इस गोपनीयता नीति से सहमत हैं। प्रश्नों के लिए कृपया <Link to="/contact">संपर्क करें</Link> या <Link to="/about">हमारे बारे में</Link> पृष्ठ देखें।</>
        }
      </p>
    </div>
  );
};

export default Privacypolicy;
